import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { TradingSymbol } from "@/utils/mockData";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useIsMobile } from "@/hooks/use-mobile";
import { useTheme } from "@/context/ThemeContext";
import { X } from "lucide-react";
// Declare global TradingView type
declare global {
  interface Window {
    TradingView: any;
  }
}
import { 
  LineChart, 
  Line, 
  BarChart as RechartsBarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer, 
  Legend, 
  ReferenceLine,
  Rectangle,
  ComposedChart,
  Scatter
} from "recharts";
import { Button } from "@/components/ui/button";
import { LineChart as LineChartIcon, BarChart as BarChartIcon, CandlestickChart as CandlestickChartIcon } from "lucide-react";
import TradingModal from "@/components/TradingModal";

// Generate mock data for chart
const generateChartData = (symbol: TradingSymbol) => {
  const data = [];
  const now = new Date();
  const basePrice = symbol.buyPrice;
  
  // Generate 24 hours of data
  for (let i = 24; i >= 0; i--) {
    const time = new Date(now);
    time.setHours(now.getHours() - i);
    
    const timeStr = time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const volatility = 0.02; // 2% volatility
    const randomChange = (Math.random() - 0.5) * volatility * basePrice;
    
    const open = basePrice + randomChange * (Math.random() * 0.8);
    const close = open + (Math.random() - 0.5) * volatility * basePrice;
    const high = Math.max(open, close) + Math.random() * volatility * basePrice * 0.5;
    const low = Math.min(open, close) - Math.random() * volatility * basePrice * 0.5;
    
    data.push({
      time: timeStr,
      price: close,
      open,
      close,
      high,
      low,
      volume: Math.floor(Math.random() * 1000000)
    });
  }
  
  return data;
};

// Custom Candlestick component for recharts
const CustomCandlestick = (props: any) => {
  const { x, y, width, height, low, high, open, close } = props;
  const isIncreasing = close > open;
  const color = isIncreasing ? "#4caf50" : "#d32f2f";
  const ratio = Math.abs(height / (open - close) || 1);
  
  return (
    <g>
      <line 
        x1={x + width / 2} 
        y1={y + height - (low - close) * ratio} 
        x2={x + width / 2} 
        y2={y - (high - close) * ratio} 
        stroke={color} 
      />
      <Rectangle
        x={x}
        y={isIncreasing ? y + height - (close - open) * ratio : y}
        width={width}
        height={Math.abs((open - close) * ratio) || 1}
        fill={color}
        stroke={color}
      />
    </g>
  );
};

interface TradingChartModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  symbol: TradingSymbol;
  defaultChartType?: "line" | "bar" | "candle";
}

const TradingChartModal: React.FC<TradingChartModalProps> = ({ open, onOpenChange, symbol, defaultChartType = "candle" }) => {
  const [chartType, setChartType] = useState<"line" | "bar" | "candle">(defaultChartType);
  const [timeframe, setTimeframe] = useState<"1h" | "4h" | "1d" | "1w">("1d");
  const [isTradingModalOpen, setIsTradingModalOpen] = useState(false);
  const [tradeType, setTradeType] = useState<"buy" | "sell">("buy");
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [tradingViewWidget, setTradingViewWidget] = useState<any>(null);
  const isMobile = useIsMobile();
  const { theme } = useTheme();
  
  const chartData = generateChartData(symbol);

  // Effect to load TradingView widget when modal opens
  useEffect(() => {
    if (open && symbol) {
      // Destroy existing widget if it exists
      if (tradingViewWidget) {
        try {
          tradingViewWidget.remove();
        } catch (e) {
          console.log('Error removing previous widget:', e);
        }
        setTradingViewWidget(null);
      }

      // Clear the container
      const container = document.getElementById("tradingview_widget_modal");
      if (container) {
        container.innerHTML = "";
      }

      // Load TradingView script if not already loaded
      if (!window.TradingView) {
        const script = document.createElement("script");
        script.src = "https://s3.tradingview.com/tv.js";
        script.onload = () => {
          setTimeout(() => createTradingViewWidget(), 100);
        };
        document.head.appendChild(script);
      } else {
        setTimeout(() => createTradingViewWidget(), 100);
      }
    }

    // Cleanup when modal closes
    if (!open && tradingViewWidget) {
      try {
        tradingViewWidget.remove();
      } catch (e) {
        console.log('Error removing widget on close:', e);
      }
      setTradingViewWidget(null);
    }
  }, [open, symbol.symbol, theme]);

  const createTradingViewWidget = () => {
    if (window.TradingView && symbol) {
      try {
        // Get the actual computed background color from CSS custom properties
        const computedStyle = getComputedStyle(document.documentElement);
        
        // Convert HSL values to hex for TradingView compatibility
        const bgHex = theme === "dark" ? "#020617" : "#ffffff";
        const textColor = theme === "dark" ? "#94a3b8" : "#475569";
        
        const widget = new window.TradingView.widget({
          container_id: "tradingview_widget_modal",
          width: "100%",
          height: isMobile ? 400 : 500,
          symbol: symbol.symbol.toUpperCase(),
          interval: timeframe === "1h" ? "60" : timeframe === "4h" ? "240" : timeframe === "1d" ? "D" : "W",
          timezone: "Etc/UTC",
          theme: theme === "dark" ? "dark" : "light",
          style: "1",
          locale: "en",
          enable_publishing: false,
          hide_side_toolbar: isMobile,
          allow_symbol_change: false,
          hide_top_toolbar: isMobile,
          hide_legend: isMobile,
          save_image: false,
          studies: [],
          overrides: {
            "paneProperties.background": bgHex,
            "paneProperties.backgroundType": "solid",
            "paneProperties.vertGridProperties.color": theme === "dark" ? "#363636" : "#e5e7eb",
            "paneProperties.horzGridProperties.color": theme === "dark" ? "#363636" : "#e5e7eb",
            "symbolWatermarkProperties.transparency": 90,
            "scalesProperties.backgroundColor": bgHex,
            "scalesProperties.textColor": textColor,
            "mainSeriesProperties.candleStyle.upColor": "#00C851",
            "mainSeriesProperties.candleStyle.downColor": "#ff4444",
            "mainSeriesProperties.candleStyle.borderUpColor": "#00C851",
            "mainSeriesProperties.candleStyle.borderDownColor": "#ff4444",
            "mainSeriesProperties.candleStyle.wickUpColor": "#00C851",
            "mainSeriesProperties.candleStyle.wickDownColor": "#ff4444"
          }
        });
        setTradingViewWidget(widget);
      } catch (error) {
        console.error('Error creating TradingView widget:', error);
      }
    }
  };
  
  const handleBuyClick = () => {
    setTradeType("buy");
    setIsTradingModalOpen(true);
  };
  
  const handleSellClick = () => {
    setTradeType("sell");
    setIsTradingModalOpen(true);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={`${isMobile ? 'max-w-[100vw] w-[100vw] h-[100vh] max-h-[100vh] p-0 m-0 border-0' : 'max-w-[100vw] w-[100vw] h-[100vh] max-h-[100vh] p-0 m-0'} overflow-hidden bg-background text-foreground border-border`}>
        <DialogHeader className={`${isMobile ? 'p-2 border-b bg-background/95 backdrop-blur-sm sticky top-0 z-10' : 'p-4 border-b'}`}>
          <DialogTitle className="flex justify-between items-center">
            <span className={`${isMobile ? 'text-sm' : 'text-base'}`}>
              {symbol.name} Chart {symbol.change >= 0 ? 
                <span className="text-positive">+{symbol.change}%</span> : 
                <span className="text-negative">{symbol.change}%</span>
              }
            </span>
            <div className="flex gap-2">
              {isMobile && (
                <Button 
        variant="ghost" 
        size="icon" 
        onClick={() => onOpenChange(false)}
        className="ml-1"
        aria-label="Close"
      >
        <X size={18} />
      </Button>
              )}
              {/* <Button 
                variant={chartType === "candle" ? "default" : "outline"} 
                size="sm" 
                onClick={() => setChartType("candle")}
                className={`${isMobile ? 'text-xs px-2' : ''}`}
              >
                <CandlestickChartIcon size={isMobile ? 12 : 14} className="mr-1" />
                Candle
              </Button> */}
            </div>
          </DialogTitle>
        </DialogHeader>
        
        <div className={`flex-1 flex flex-col ${isMobile ? 'h-[calc(100vh-60px)]' : 'h-[calc(100vh-120px)]'} overflow-hidden`}>
          <Tabs defaultValue="1d" className="w-full h-full flex flex-col">
            <TabsList className={`${isMobile ? 'mx-2 mt-1 mb-1 h-8' : 'mx-4 mt-2 mb-2'} shrink-0`}>
              <TabsTrigger value="1h" onClick={() => setTimeframe("1h")} className={`${isMobile ? 'text-xs px-2 h-6' : ''}`}>1H</TabsTrigger>
              <TabsTrigger value="4h" onClick={() => setTimeframe("4h")} className={`${isMobile ? 'text-xs px-2 h-6' : ''}`}>4H</TabsTrigger>
              <TabsTrigger value="1d" onClick={() => setTimeframe("1d")} className={`${isMobile ? 'text-xs px-2 h-6' : ''}`}>1D</TabsTrigger>
              <TabsTrigger value="1w" onClick={() => setTimeframe("1w")} className={`${isMobile ? 'text-xs px-2 h-6' : ''}`}>1W</TabsTrigger>
            </TabsList>
            
            <TabsContent value="1h" className="flex-1 m-0 p-0 h-full">
              <div className={`w-full h-full ${isMobile ? 'px-1 pb-1' : 'px-4 pb-2'}`}>
                {isMobile ? (
                  <div id="tradingview_widget_modal" className="w-full h-full bg-background"></div>
                ) : (
                  renderChart(chartType, chartData, isMobile, isFullScreen, theme)
                )}
              </div>
            </TabsContent>
            <TabsContent value="4h" className="flex-1 m-0 p-0 h-full">
              <div className={`w-full h-full ${isMobile ? 'px-1 pb-1' : 'px-4 pb-2'}`}>
                {isMobile ? (
                  <div id="tradingview_widget_modal" className="w-full h-full bg-background"></div>
                ) : (
                  renderChart(chartType, chartData, isMobile, isFullScreen, theme)
                )}
              </div>
            </TabsContent>
            <TabsContent value="1d" className="flex-1 m-0 p-0 h-full">
              <div className={`w-full h-full ${isMobile ? 'px-1 pb-1' : 'px-4 pb-2'}`}>
                {isMobile ? (
                  <div id="tradingview_widget_modal" className="w-full h-full bg-background"></div>
                ) : (
                  renderChart(chartType, chartData, isMobile, isFullScreen, theme)
                )}
              </div>
            </TabsContent>
            <TabsContent value="1w" className="flex-1 m-0 p-0 h-full">
              <div className={`w-full h-full ${isMobile ? 'px-1 pb-1' : 'px-4 pb-2'}`}>
                {isMobile ? (
                  <div id="tradingview_widget_modal" className="w-full h-full bg-background"></div>
                ) : (
                  renderChart(chartType, chartData, isMobile, isFullScreen, theme)
                )}
              </div>
            </TabsContent>
          </Tabs>
        </div>
        
        {/* Trading Stats - Compact Bottom Bar */}
        <div className={`${isMobile ? 'px-2 py-1 bg-background/95 backdrop-blur-sm border-t' : 'px-4 py-2 border-t'} shrink-0`}>
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-1">
              <span className={`${isMobile ? 'text-xs' : 'text-sm'} text-muted-foreground`}>Price:</span>
              <span className={`${isMobile ? 'text-sm' : 'text-base'} font-semibold`}>{symbol.buyPrice.toFixed(2)}</span>
            </div>
            <div className="flex items-center gap-1">
              <span className={`${isMobile ? 'text-xs' : 'text-sm'} text-muted-foreground`}>24h:</span>
              <span className={`${isMobile ? 'text-sm' : 'text-base'} font-semibold ${symbol.change >= 0 ? "text-positive" : "text-negative"}`}>
                {symbol.change >= 0 ? "+" : ""}{symbol.change}%
              </span>
            </div>
          </div>
        </div>
        
        {/* Trading Modal */}
        {isTradingModalOpen && (
          <TradingModal
            open={isTradingModalOpen}
            onOpenChange={setIsTradingModalOpen}
            symbol={symbol}
            tradeType={tradeType}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

// Helper function to render the appropriate chart based on type
const renderChart = (type: "line" | "bar" | "candle", data: any[], isMobile: boolean = false, isFullScreen: boolean = false, theme: "dark" | "light") => {
  const chartHeight = "100%";
  
  // Theme-based colors using semantic tokens
  const gridColor = "hsl(var(--border))";
  const textColor = "hsl(var(--muted-foreground))";
  const tooltipBg = "hsl(var(--background))";
  const tooltipBorder = "hsl(var(--border))";
  
  switch (type) {
    case "line":
      return (
        <ResponsiveContainer width="100%" height={chartHeight}>
          <LineChart data={data} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
            <XAxis dataKey="time" tick={{ fill: textColor }} />
            <YAxis domain={['auto', 'auto']} tick={{ fill: textColor }} />
            <Tooltip 
              contentStyle={{
                backgroundColor: tooltipBg,
                border: `1px solid ${tooltipBorder}`,
                borderRadius: '4px',
                color: textColor
              }}
            />
            <Legend />
            <Line type="monotone" dataKey="price" stroke="hsl(262.1 83.3% 57.8%)" dot={false} />
          </LineChart>
        </ResponsiveContainer>
      );
    case "bar":
      return (
        <ResponsiveContainer width="100%" height={chartHeight}>
          <RechartsBarChart data={data} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
            <XAxis dataKey="time" tick={{ fill: textColor }} />
            <YAxis domain={['auto', 'auto']} tick={{ fill: textColor }} />
            <Tooltip 
              contentStyle={{
                backgroundColor: tooltipBg,
                border: `1px solid ${tooltipBorder}`,
                borderRadius: '4px',
                color: textColor
              }}
            />
            <Legend />
            <Bar dataKey="price" fill="hsl(262.1 83.3% 57.8%)" />
          </RechartsBarChart>
        </ResponsiveContainer>
      );
    case "candle":
      return (
        <div className="w-full h-full overflow-hidden">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart 
              data={data} 
              margin={{ 
                top: 10, 
                right: isMobile ? 5 : 20, 
                bottom: isMobile ? 5 : 10, 
                left: isMobile ? 5 : 10 
              }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={gridColor} opacity={0.3} />
              <XAxis 
                dataKey="time" 
                fontSize={isMobile ? 8 : 12}
                interval={isMobile ? Math.floor(data.length / 4) : Math.floor(data.length / 8)}
                tick={{ fill: textColor }}
              />
              <YAxis 
                domain={['dataMin - 0.001', 'dataMax + 0.001']} 
                fontSize={isMobile ? 8 : 12}
                width={isMobile ? 35 : 60}
                tick={{ fill: textColor }}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: tooltipBg,
                  border: `1px solid ${tooltipBorder}`,
                  borderRadius: '4px',
                  fontSize: isMobile ? '10px' : '12px',
                  color: textColor
                }}
                formatter={(value: any, name: any) => {
                  if (name === 'open' || name === 'high' || name === 'low' || name === 'close') {
                    return [Number(value).toFixed(2), name.charAt(0).toUpperCase() + name.slice(1)];
                  }
                  return [value, name];
                }}
                labelFormatter={(label) => `Time: ${label}`}
              />
              {data.map((entry, index) => (
                <Bar 
                  key={index}
                  dataKey={() => Math.abs(entry.close - entry.open)}
                  x={index * (100 / data.length)}
                  fill={entry.close > entry.open ? "#4caf50" : "#d32f2f"}
                  radius={[1, 1, 1, 1]}
                />
              ))}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      );
    default:
      return null;
  }
};

export default TradingChartModal;